/**
 * The public account-deletion page (`/request-account-deletion`).
 *
 * Deliberately reachable by anyone: no session, no guard, no app install. That is the whole
 * requirement — the store policies want an account holder who has already deleted the app, or
 * who never had it on this device, to be able to ask for their account to go. Which is why it
 * is a form asking who you are rather than a button: nothing here proves the sender owns the
 * account they are naming, so an admin verifies before anything is closed.
 *
 * The two columns carry different jobs. The left one is the disclosure — what goes, what
 * stays, and how long it takes — and exists so nobody submits this without knowing what they
 * are asking for. The right one is the form.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2, PawPrint, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { TextField, TextAreaField } from '@/components/text-field';
import { ApiError } from '@/lib/api';
import { use_deletion_policy, use_submit_public_deletion } from '@/features/account-deletion/hooks';
import type { DeletionSubjectType } from '@/features/account-deletion/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The fallback window, used only until `/account-deletion/policy` answers. */
const FALLBACK_GRACE_DAYS = 15;

const DELETED = [
  'Profile information (name, email, phone number)',
  'Your pets, their photos and medical records',
  'Saved addresses and favourites',
  'Cart contents and stock alerts',
  'Account credentials and session data',
  'Marketing preferences and notification subscriptions',
];

const KEPT = [
  'Booking, order and payment records we are required to keep for tax and accounting',
  'Money already captured against a booking that has not been completed or refunded',
  'Reviews you left, anonymised so they no longer identify you',
];

export function RequestAccountDeletionPage() {
  const navigate = useNavigate();
  const policy_q = use_deletion_policy();
  const submit = use_submit_public_deletion();

  const [full_name, set_full_name] = useState('');
  const [email, set_email] = useState('');
  const [phone, set_phone] = useState('');
  const [subject_type, set_subject_type] = useState<DeletionSubjectType>('USER');
  const [reason, set_reason] = useState('');
  const [confirmed, set_confirmed] = useState(false);
  const [errors, set_errors] = useState<{ full_name?: string; email?: string }>({});

  const grace_days = policy_q.data?.grace_days ?? FALLBACK_GRACE_DAYS;

  function validate(): boolean {
    const next: typeof errors = {};
    if (full_name.trim().length < 2) next.full_name = 'Please enter your full name.';
    if (!EMAIL_RE.test(email.trim())) next.email = 'Please enter a valid email address.';
    set_errors(next);
    return Object.keys(next).length === 0;
  }

  function on_submit(e: FormEvent) {
    e.preventDefault();
    if (!validate() || !confirmed || submit.isPending) return;
    submit.mutate({
      full_name: full_name.trim(),
      email: email.trim(),
      phone: phone.trim() || undefined,
      subject_type,
      reason: reason.trim() || undefined,
    });
  }

  const error_message =
    submit.error instanceof ApiError
      ? submit.error.message
      : submit.error
        ? 'Something went wrong. Please try again.'
        : null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b-2 border-brand-outline-variant/20 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 w-full max-w-5xl items-center justify-between gap-4 px-5">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center gap-2.5"
            data-testid="deletion-home"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-button accent-gradient text-white shadow-clay-sm">
              <PawPrint className="h-5 w-5" />
            </span>
            <span className="font-heading text-lg font-bold tracking-tight">Pet Marketplace</span>
          </button>
        </div>
      </header>

      <main
        className="mx-auto w-full max-w-5xl px-5 py-10 sm:py-14"
        data-testid="request-account-deletion"
      >
        <div className="text-center">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
            Request account deletion
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
            Pet Marketplace — delete your account and the personal data attached to it. You do not
            need the app installed to use this page.
          </p>
        </div>

        {submit.isSuccess ? (
          <SubmittedPanel
            reference={submit.data.reference}
            message={submit.data.message}
            on_home={() => navigate('/')}
          />
        ) : (
          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <div className="flex flex-col gap-6">
              <Card>
                <CardContent className="p-6">
                  <h2 className="flex items-center gap-2 font-heading text-xl font-bold">
                    <AlertCircle className="h-5 w-5 text-destructive" />
                    How to request deletion
                  </h2>
                  <ol className="mt-4 flex list-decimal flex-col gap-3 pl-5 text-sm text-muted-foreground">
                    <li>Fill in the form using the details registered on your account.</li>
                    <li>
                      Tick the confirmation to say you understand deletion is permanent, and
                      submit.
                    </li>
                    <li>
                      Our team verifies who you are. We may call or message the number on the
                      account before going ahead.
                    </li>
                    <li>
                      Once verified, your account is closed immediately and permanently deleted
                      within {grace_days} days. You can contact support to cancel at any point
                      before then.
                    </li>
                  </ol>
                  <p className="mt-4 border-t-2 border-brand-outline-variant/20 pt-4 text-xs text-muted-foreground">
                    You can also do this from inside the app, under Profile &rarr; Settings, where
                    your identity is already confirmed and no verification call is needed.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h2 className="font-heading text-lg font-bold">Data that will be deleted</h2>
                  <ul className="mt-3 flex list-disc flex-col gap-2 pl-5 text-sm text-muted-foreground">
                    {DELETED.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h2 className="font-heading text-lg font-bold">Data that may be kept</h2>
                  <ul className="mt-3 flex list-disc flex-col gap-2 pl-5 text-sm text-muted-foreground">
                    {KEPT.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Card className="h-fit lg:sticky lg:top-6">
              <CardContent className="p-6">
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold">
                  <Trash2 className="h-5 w-5 text-destructive" />
                  Deletion request form
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Use the email and phone number registered on your Pet Marketplace account.
                </p>

                <form className="mt-6 flex flex-col gap-5" onSubmit={on_submit} noValidate>
                  <TextField
                    label="Full name"
                    name="full_name"
                    autoComplete="name"
                    value={full_name}
                    error={errors.full_name}
                    onChange={(e) => set_full_name(e.target.value)}
                    data-testid="deletion-full-name"
                  />
                  <TextField
                    label="Account email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    error={errors.email}
                    onChange={(e) => set_email(e.target.value)}
                    data-testid="deletion-email"
                  />
                  <TextField
                    label="Phone number"
                    name="phone"
                    type="tel"
                    optional
                    autoComplete="tel"
                    hint="This is how most accounts sign in, so it is the fastest way for us to find yours."
                    value={phone}
                    onChange={(e) => set_phone(e.target.value)}
                    data-testid="deletion-phone"
                  />

                  <fieldset className="flex flex-col gap-2">
                    <legend className="text-sm font-semibold">Account type</legend>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      {(
                        [
                          { value: 'USER', label: 'Pet owner account' },
                          { value: 'VENDOR', label: 'Vendor / business account' },
                        ] as const
                      ).map((opt) => (
                        <label
                          key={opt.value}
                          className={
                            'flex flex-1 cursor-pointer items-center gap-2 rounded-button border-2 px-3 py-2.5 text-sm font-medium transition-colors ' +
                            (subject_type === opt.value
                              ? 'border-brand-navy bg-secondary'
                              : 'border-brand-outline-variant/30 hover:bg-secondary')
                          }
                        >
                          <input
                            type="radio"
                            name="subject_type"
                            className="h-4 w-4 accent-primary"
                            checked={subject_type === opt.value}
                            onChange={() => set_subject_type(opt.value)}
                            data-testid={`deletion-type-${opt.value.toLowerCase()}`}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <TextAreaField
                    label="Reason"
                    name="reason"
                    optional
                    rows={4}
                    placeholder="Tell us why you are leaving (optional)"
                    value={reason}
                    onChange={(e) => set_reason(e.target.value)}
                    data-testid="deletion-reason"
                  />

                  <label className="flex cursor-pointer items-start gap-3 rounded-button border-2 border-brand-outline-variant/30 p-3">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-5 w-5 shrink-0 rounded border-input accent-primary"
                      checked={confirmed}
                      onChange={(e) => set_confirmed(e.target.checked)}
                      data-testid="deletion-confirm"
                    />
                    <span className="text-sm text-muted-foreground">
                      I understand that deleting my account is permanent and cannot be undone. I
                      want to delete my Pet Marketplace account and the personal data attached to
                      it.
                    </span>
                  </label>

                  {error_message && (
                    <p
                      className="rounded-button border-2 border-destructive p-3 text-sm text-destructive"
                      data-testid="deletion-error"
                    >
                      {error_message}
                    </p>
                  )}

                  <Button
                    type="submit"
                    variant="destructive"
                    className="w-full gap-2"
                    // Disabled until the tick, because the tick IS the consent record — the
                    // API refuses a submission without it, and a button that fires a doomed
                    // request reads as a broken page.
                    disabled={!confirmed || submit.isPending}
                    data-testid="deletion-submit"
                  >
                    {submit.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                    Submit deletion request
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}

/**
 * What replaces the form once it is in.
 *
 * The reference number is the point: the promise says "contact support if you wish to cancel",
 * and support cannot find anything from "I filled in a form last week".
 */
function SubmittedPanel({
  reference,
  message,
  on_home,
}: {
  reference: string;
  message: string;
  on_home: () => void;
}) {
  return (
    <Card className="mx-auto mt-10 max-w-2xl" data-testid="deletion-submitted">
      <CardContent className="p-8 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary">
          <CheckCircle2 className="h-7 w-7 text-success" />
        </span>
        <h2 className="mt-5 font-heading text-2xl font-bold">Request received</h2>
        <p className="mx-auto mt-3 max-w-lg text-sm text-muted-foreground">{message}</p>
        <div className="mt-6 rounded-button border-2 border-brand-outline-variant/30 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Your reference
          </p>
          <p className="mt-1 break-all font-mono text-sm font-semibold" data-testid="deletion-reference">
            {reference}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Quote this if you contact support about the request.
          </p>
        </div>
        <Button variant="secondary" className="mt-6" onClick={on_home}>
          Back to home
        </Button>
      </CardContent>
    </Card>
  );
}
